import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Axios from "axios";

import { loginThunk, resetLogin } from "@/features/auth/store/authSlice";
import {
  selectedBrandSelector,
  setGlobalBrand,
} from "@/shared/store/brandSlice";
import {
  isLoggedInSelector,
  authLoadingSelector as isLoadingSelector,
} from "@/features/auth/store/authSlice";
import { GlobalContext } from "@/helpers/contexts/contexts";
import {
  MAINTENANCE_MODE,
  MAINTENANCE_MODE_ALLOWED_IPS,
  MAINTENANCE_MODE_ALLOWED_COUNTRY,
  MAINTENANCE_MODE_COUNTRY,
  IPINFO_API_URL,
  IPIFY_API_URL,
} from "@/config/appConfig";
import { reloadOtherTabs } from "@/shared/helpers/authHelper";
import { BRAND_CONST, USER_ROLE } from "@/config/constants";
import { useAppTheme } from "@/helpers/contexts/themeContext";
import { encodePassword } from "@/helpers/utils";
import { loginWithToken } from "@/shared/helpers/tokenBasedLoginHelper";
import { logger } from "@/helpers/loggerHelper";
import { ENV } from "@/config/appConfig";
import { ROUTES } from "@/helpers/routes/routePaths";
import { ROLES } from "@/helpers/routes/rolesConfig";
import { getRedirectPath } from "@/helpers/localStore";

type LoginValues = {
  password: string;
  [key: string]: unknown;
};

export function useLogin(email?: string | string[], subscribe?: unknown) {
  const dispatch = useDispatch() as any;
  const navigate = useNavigate();
  const isLoggedIn = useSelector(isLoggedInSelector);
  const isLoading = useSelector(isLoadingSelector);
  const selectedBrand = useSelector(selectedBrandSelector);
  const selectedBrandId = selectedBrand?.id;
  const { userBrands, loggedInUserRole, loggedInUser } = useContext(
    GlobalContext
  ) as any;
  const { getTheme } = useAppTheme() as any;
  const [maintenance, setMaintenance] = useState(MAINTENANCE_MODE);
  const savedRedirectPath = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      dispatch(resetLogin());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function allowIP() {
      try {
        if (MAINTENANCE_MODE_COUNTRY) {
          const geoUrl = new URL(IPINFO_API_URL);
          geoUrl.searchParams.set("token", ENV.PUBLIC_IPINFO_TOKEN);
          const geoRes = await Axios.get(geoUrl.toString());
          const country = geoRes.data.country_code || geoRes.data.country;
          if (MAINTENANCE_MODE_ALLOWED_COUNTRY.includes(country)) {
            setMaintenance(false);
          }
        }

        const ipRes = await Axios.get(IPIFY_API_URL);
        const ipAdd = ipRes.data.ip;
        if (MAINTENANCE_MODE_ALLOWED_IPS.includes(ipAdd)) {
          setMaintenance(false);
        }
      } catch (err) {
        logger.error("Error in maintenance", err);
      }
    }
    if (maintenance) {
      allowIP();
    }
  }, [maintenance]);

  useEffect(() => {
    if (!isLoggedIn) {
      savedRedirectPath.current = null;
      return;
    }
    if (isLoggedIn && loggedInUserRole && loggedInUser?.id) {
      if (selectedBrandId) {
        const redirectPath = savedRedirectPath.current || getRedirectPath();
        if (redirectPath) {
          savedRedirectPath.current = redirectPath;
          return navigate(redirectPath, { replace: true });
        }
      }

      const isFirstTimeLogin = loggedInUser.user_login_count === 0;
      const firstBrand = userBrands?.[0];
      if (
        (ROLES.PARTICIPANT.includes(loggedInUserRole) ||
          loggedInUserRole === USER_ROLE.BRAND_USER.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
          loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME) &&
        userBrands
      ) {
        if (isFirstTimeLogin || subscribe) {
          if (
            loggedInUserRole === USER_ROLE.BRAND_USER.NAME &&
            firstBrand.id !== BRAND_CONST.KUMON_BRAND_ID
          ) {
            return navigate(ROUTES.DASHBOARD, { replace: true });
          }
          loginWithToken({ data: firstBrand });
          dispatch(setGlobalBrand(firstBrand));
          getTheme(firstBrand.id);
          const redirectPath = savedRedirectPath.current || getRedirectPath();
          if (redirectPath) {
            savedRedirectPath.current = redirectPath;
            return navigate(redirectPath, { replace: true });
          }
          if (firstBrand.has_active_subscription_page) {
            return navigate(ROUTES.SUBSCRIPTION_PACKAGES, { replace: true });
          }
        }
      }

      return navigate(ROUTES.HOME, { replace: true });
    }
  }, [
    isLoggedIn,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    userBrands?.length,
    userBrands?.[0]?.id,
    loggedInUserRole,
    loggedInUser?.id,
    loggedInUser?.user_login_count,
    subscribe,
    selectedBrandId,
  ]);

  useEffect(() => {
    if (isLoggedIn) {
      reloadOtherTabs();
    }
  }, [isLoggedIn]);

  const login = async (values: LoginValues) => {
    try {
      const loginData = {
        ...values,
        password: encodePassword(values.password),
      };
      dispatch(loginThunk(loginData));
    } catch (error) {
      dispatch(loginThunk(values));
    }
  };

  return {
    login,
    isLoading,
    maintenance,
  };
}
